import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Star, Plus, Robot, SpinnerGap, GoogleLogo, CheckCircle, Warning, LinkSimple } from '@phosphor-icons/react';

export default function ReviewsPage() {
  const { api } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ property_id: '', guest_name: '', rating: 5, review_text: '', platform: 'airbnb' });
  const [generating, setGenerating] = useState(null);
  const [posting, setPosting] = useState(null);
  const [googleStatus, setGoogleStatus] = useState({ configured: false, connected: false, locations: [] });
  const [showLinkModal, setShowLinkModal] = useState(null); // property object
  const [banner, setBanner] = useState(null);

  const loadAll = () => {
    Promise.all([api.get('/reviews'), api.get('/properties'), api.get('/google/status')])
      .then(([r, p, g]) => { setReviews(r.data); setProperties(p.data); setGoogleStatus(g.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    loadAll();
    // Handle OAuth redirect ?google_connected=1 / ?google_error=...
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected')) {
      setBanner({ type: 'success', msg: 'Connected to Google Business Profile successfully.' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('google_error')) {
      setBanner({ type: 'error', msg: `Google connection failed: ${params.get('google_error')}` });
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    await api.post('/reviews', form);
    setShowForm(false);
    setForm({ property_id: '', guest_name: '', rating: 5, review_text: '', platform: 'airbnb' });
    loadAll();
  };

  const generateResponse = async (reviewId) => {
    setGenerating(reviewId);
    try {
      await api.post(`/reviews/${reviewId}/ai-response`);
      loadAll();
    } catch { alert('Failed'); } finally { setGenerating(null); }
  };

  const connectGoogle = async () => {
    try {
      const { data } = await api.get('/google/login');
      window.location.href = data.auth_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Google integration not available yet.';
      setBanner({ type: 'error', msg: detail });
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect your Google Business Profile account?')) return;
    await api.post('/google/disconnect');
    loadAll();
  };

  const refreshLocations = async () => {
    try {
      const { data } = await api.post('/google/refresh-locations');
      setGoogleStatus(s => ({ ...s, locations: data.locations }));
      setBanner({ type: 'success', msg: `Loaded ${data.locations.length} Google Business location(s).` });
    } catch (e) {
      setBanner({ type: 'error', msg: e?.response?.data?.detail || 'Failed to load locations.' });
    }
  };

  const linkLocation = async (propId, googleLocationName) => {
    await api.post(`/properties/${propId}/google-location`, { google_location_name: googleLocationName });
    const props = await api.get('/properties');
    setProperties(props.data);
    setShowLinkModal(null);
    setBanner({ type: 'success', msg: 'Property linked to Google location.' });
  };

  const postToGoogle = async (reviewId) => {
    setPosting(reviewId);
    try {
      const { data } = await api.post(`/reviews/${reviewId}/post-to-google`);
      if (data.status === 'posted') {
        setBanner({ type: 'success', msg: 'Reply published live on Google.' });
      } else {
        const reason = data.reason === 'not_connected' ? 'Connect Google first.' : data.reason === 'no_location_linked' ? 'Link this property to a Google location first.' : 'Queued — will post after next Google sync.';
        setBanner({ type: 'info', msg: reason });
      }
      loadAll();
    } catch (e) {
      setBanner({ type: 'error', msg: e?.response?.data?.detail || 'Failed to post to Google.' });
    } finally {
      setPosting(null);
    }
  };

  const GoogleBar = () => (
    <div className="bg-white border border-zinc-200 p-4 mb-4 flex items-start justify-between gap-4" data-testid="google-connect-bar">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 flex items-center justify-center rounded-sm ${googleStatus.connected ? 'bg-emerald-50' : 'bg-zinc-100'}`}>
          <GoogleLogo size={20} weight="bold" className={googleStatus.connected ? 'text-emerald-600' : 'text-zinc-500'} />
        </div>
        <div>
          <p className="text-sm font-bold text-zinc-900">
            {googleStatus.connected ? `Connected: ${googleStatus.google_email}` : 'Connect your Google Business Profile'}
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">
            {googleStatus.connected
              ? 'Push 4-5 star reviews as public replies to your Google listing.'
              : 'One-click connect to publish great reviews and replies to your Google listing.'}
          </p>
          {googleStatus.connected && googleStatus.locations?.length > 0 && (
            <p className="text-xs text-zinc-500 mt-1">{googleStatus.locations.length} Google location(s) available.</p>
          )}
          {!googleStatus.configured && (
            <p className="text-xs text-amber-600 mt-1">Platform credentials are still being provisioned. You can preview the flow; live posting unlocks once your Google app is approved.</p>
          )}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {googleStatus.connected ? (
          <>
            <button onClick={refreshLocations} className="px-3 py-2 border border-zinc-200 text-xs font-bold rounded-sm hover:bg-zinc-50" data-testid="refresh-google-locations">Refresh locations</button>
            <button onClick={disconnectGoogle} className="px-3 py-2 border border-zinc-200 text-xs font-bold rounded-sm hover:bg-zinc-50 text-rose-600" data-testid="disconnect-google">Disconnect</button>
          </>
        ) : (
          <button onClick={connectGoogle} className="px-4 py-2 bg-zinc-900 text-white text-xs font-bold rounded-sm hover:bg-zinc-800 flex items-center gap-2" data-testid="connect-google-btn">
            <GoogleLogo size={14} weight="bold" /> Connect Google
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="reviews-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Reviews</h1>
          <p className="text-sm text-zinc-500 mt-1">Log reviews, draft AI replies, publish the best ones to Google.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] flex items-center gap-2" data-testid="add-review-btn">
          <Plus weight="bold" size={14} /> Log Review
        </button>
      </div>

      {banner && (
        <div className={`mb-4 px-4 py-3 text-sm rounded-sm border flex items-start gap-2 ${banner.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : banner.type === 'error' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`} data-testid="google-banner">
          {banner.type === 'success' ? <CheckCircle size={16} weight="fill" /> : <Warning size={16} weight="fill" />}
          <span className="flex-1">{banner.msg}</span>
          <button onClick={() => setBanner(null)} className="text-xs font-bold opacity-70 hover:opacity-100">Dismiss</button>
        </div>
      )}

      <GoogleBar />

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowForm(false)} />
          <div className="relative bg-white border border-zinc-200 w-full max-w-lg">
            <div className="p-6 border-b border-zinc-200">
              <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Log Guest Review</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <select value={form.property_id} onChange={e => set('property_id', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm bg-white" data-testid="review-property">
                <option value="">Select property</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input value={form.guest_name} onChange={e => set('guest_name', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" placeholder="Guest name" data-testid="review-guest" />
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Rating</label>
                <div className="flex gap-1">{[1,2,3,4,5].map(n => (
                  <button key={n} type="button" onClick={() => set('rating', n)} className="p-1" data-testid={`review-star-${n}`}>
                    <Star size={20} weight={n <= form.rating ? 'fill' : 'regular'} className={n <= form.rating ? 'text-amber-400' : 'text-zinc-300'} />
                  </button>
                ))}</div>
              </div>
              <textarea value={form.review_text} onChange={e => set('review_text', e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm resize-none" placeholder="Guest review text..." data-testid="review-text" />
              <select value={form.platform} onChange={e => set('platform', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm bg-white" data-testid="review-platform">
                <option value="airbnb">Airbnb</option>
                <option value="vrbo">Vrbo</option>
                <option value="booking">Booking.com</option>
                <option value="google">Google</option>
                <option value="direct">Direct</option>
              </select>
              <div className="flex gap-3"><button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-zinc-200 text-sm font-bold rounded-sm">Cancel</button>
              <button type="submit" className="flex-1 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm" data-testid="review-save">Save</button></div>
            </form>
          </div>
        </div>
      )}

      {showLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowLinkModal(null)} />
          <div className="relative bg-white border border-zinc-200 w-full max-w-lg" data-testid="link-location-modal">
            <div className="p-6 border-b border-zinc-200">
              <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Link "{showLinkModal.name}" to Google Business location</h2>
              <p className="text-sm text-zinc-500 mt-1">Pick which Google listing this property maps to.</p>
            </div>
            <div className="p-6 space-y-2 max-h-80 overflow-y-auto">
              {googleStatus.locations.length === 0 && <p className="text-sm text-zinc-500">No locations loaded yet. Click "Refresh locations" above.</p>}
              {googleStatus.locations.map((loc) => (
                <button key={loc.name} onClick={() => linkLocation(showLinkModal.id, loc.name)} className="w-full text-left p-3 border border-zinc-200 hover:border-[#0052FF] hover:bg-blue-50 rounded-sm" data-testid={`pick-location-${loc.name}`}>
                  <p className="text-sm font-bold text-zinc-900">{loc.title || loc.name}</p>
                  {loc.address && <p className="text-xs text-zinc-500">{loc.address}</p>}
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-zinc-200 flex justify-end">
              <button onClick={() => setShowLinkModal(null)} className="px-4 py-2 border border-zinc-200 text-sm font-bold rounded-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p className="text-sm text-zinc-500">Loading...</p> : reviews.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-16 text-center" data-testid="no-reviews">
          <Star size={40} weight="duotone" className="text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>No reviews yet</h3>
          <p className="text-sm text-zinc-500">Log guest reviews to track ratings and generate AI responses.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r, i) => {
            const matchedProp = properties.find(p => p.id === r.property_id);
            const hasLinkedLocation = !!matchedProp?.google_location_name;
            const canPostToGoogle = r.rating >= 4 && r.ai_response;
            return (
            <div key={r.id} className="bg-white border border-zinc-200 p-5" data-testid={`review-${i}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-bold text-zinc-900">{r.guest_name}</p>
                    <span className="text-xs px-2 py-0.5 bg-zinc-100 text-zinc-600 font-bold rounded-sm">{r.platform}</span>
                    {r.google_status === 'posted' && <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 font-bold rounded-sm flex items-center gap-1"><GoogleLogo size={10} weight="bold" /> Posted</span>}
                    {r.google_status === 'queued' && <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 font-bold rounded-sm flex items-center gap-1"><GoogleLogo size={10} weight="bold" /> Queued</span>}
                  </div>
                  <p className="text-xs text-zinc-500">{r.property_name}</p>
                </div>
                <div className="flex gap-0.5">{[...Array(5)].map((_, j) => <Star key={j} size={12} weight={j < r.rating ? 'fill' : 'regular'} className={j < r.rating ? 'text-amber-400' : 'text-zinc-200'} />)}</div>
              </div>
              {r.review_text && <p className="text-sm text-zinc-700 mb-3">"{r.review_text}"</p>}
              {r.ai_response ? (
                <div className="bg-blue-50 border border-blue-100 p-3 rounded-sm">
                  <p className="text-xs font-bold text-blue-700 mb-1 flex items-center gap-1"><Robot size={12} /> AI-Drafted Response</p>
                  <p className="text-sm text-blue-900">{r.ai_response}</p>
                </div>
              ) : (
                <button onClick={() => generateResponse(r.id)} disabled={generating === r.id} className="px-3 py-1.5 bg-[#0052FF] text-white text-xs font-bold rounded-sm hover:bg-[#003EDD] flex items-center gap-1.5" data-testid={`generate-response-${i}`}>
                  {generating === r.id ? <><SpinnerGap size={12} className="animate-spin" /> Drafting...</> : <><Robot size={12} /> AI Draft Response</>}
                </button>
              )}

              {canPostToGoogle && r.google_status !== 'posted' && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => postToGoogle(r.id)}
                    disabled={posting === r.id}
                    className="px-3 py-1.5 bg-zinc-900 text-white text-xs font-bold rounded-sm hover:bg-zinc-800 flex items-center gap-1.5"
                    data-testid={`post-to-google-${i}`}
                  >
                    {posting === r.id ? <><SpinnerGap size={12} className="animate-spin" /> Posting...</> : <><GoogleLogo size={12} weight="bold" /> Post reply to Google</>}
                  </button>
                  {googleStatus.connected && !hasLinkedLocation && matchedProp && (
                    <button onClick={() => setShowLinkModal(matchedProp)} className="px-3 py-1.5 border border-zinc-200 text-xs font-bold rounded-sm hover:bg-zinc-50 flex items-center gap-1.5" data-testid={`link-location-${i}`}>
                      <LinkSimple size={12} weight="bold" /> Link property to Google
                    </button>
                  )}
                  {!googleStatus.connected && <span className="text-xs text-zinc-500">(Connect Google above to publish live)</span>}
                </div>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
}
